import type { ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { ProcessCatalogue } from "../../../api/content/processDefinitions";
import ChangeRow from "../../../ui/content/ChangeRow";
import type { ChangeKind } from "../../../ui/styles/changeStyles";
import { ItemReferenceName } from "../../items";
import StepPortName, { StepName } from "../model/StepPortName";
import { expandableNodeIds, workflowGraph, type GraphNode } from "../model/workflowGraph";
import { definitionOfDraft } from "../model/workflowDraft";
import type { PlacedUse, WorkflowChange } from "../model/workflowChanges";
import { shownValue } from "../../threads/stepPlans/planText";

const KIND: Readonly<Record<WorkflowChange["kind"], ChangeKind>> = {
  stepAdded: "added",
  stepRemoved: "removed",
  stepRepinned: "changed",
  definitionBound: "changed",
  roleChanged: "changed",
  startChanged: "changed",
  stepMoved: "changed",
  bindingAdded: "added",
  bindingRemoved: "removed",
  bindingChanged: "changed",
  exportAdded: "added",
  exportRemoved: "removed",
  exportChanged: "changed",
  textsChanged: "changed",
  defaultAdded: "added",
  defaultChanged: "changed",
  defaultRemoved: "removed",
};

interface WorkflowChangeListProps {
  changes: readonly WorkflowChange[];
  before: WorkflowContent;
  after: WorkflowContent;
  catalogue: ProcessCatalogue;
}

/** The draft's changes, naming pinned steps and the ports their cards show (AU-6). */
export default function WorkflowChangeList({ changes, before, after, catalogue }: WorkflowChangeListProps) {
  const { t } = useTranslation();
  if (changes.length === 0) {
    return <p className="app-text-muted text-xs">{t("workflows.changes.none")}</p>;
  }
  const nestedDefaults = changes.some((change) => "destination" in change && change.destination.split(".").length > 2);
  const nodesOf = (content: WorkflowContent) => {
    const workflow = definitionOfDraft(content, "draft");
    const closed = workflowGraph(workflow, catalogue, new Set(), { allOutputs: true }).nodes;
    // Closed groups name their exported ports; opened groups also name nested input defaults.
    return nestedDefaults ? [...closed, ...workflowGraph(workflow, catalogue, expandableNodeIds(workflow, catalogue), { allOutputs: true }).nodes] : closed;
  };
  const old = nodesOf(before);
  const now = nodesOf(after);
  return (
    <ul className="space-y-0.5 text-xs">
      {changes.map((change, index) => {
        return (
          <ChangeRow key={index} kind={KIND[change.kind]}>
            <ChangeText change={change} before={old} after={now} />
          </ChangeRow>
        );
      })}
    </ul>
  );
}

function ChangeText({ change, before, after }: { change: WorkflowChange; before: readonly GraphNode[]; after: readonly GraphNode[] }) {
  const { t } = useTranslation();
  const node = (nodes: readonly GraphNode[], key: string) => nodes.find((candidate) => candidate.id === key);
  const step = (key: string, nodes = after, showVersion = false) => <StepName node={node(nodes, key)} stepKey={key} showVersion={showVersion} />;
  const port = (key: string, name: string, direction: "input" | "output", nodes = after) => <StepPortName node={node(nodes, key)} stepKey={key} name={name} direction={direction} />;
  const source = (from: { from_step: string; part: string }, nodes = after) => port(from.from_step, from.part, "output", nodes);
  const destination = (address: string, nodes = after) => {
    const at = address.lastIndexOf(".");
    return at < 0 ? <code>{address}</code> : port(address.slice(0, at), address.slice(at + 1), "input", nodes);
  };
  const line = (components: Record<string, ReactElement>, values?: Record<string, unknown>, key: string = change.kind) => <Trans i18nKey={`workflows.changes.${key}`} components={components} values={values} />;
  switch (change.kind) {
    case "stepAdded":
      return line({ step: step(change.key) }, { role: t(`workflows.edit.roles.${change.role}`) }, ADDED[change.use.kind]);
    case "stepRemoved":
      return line({ step: step(change.key, before) });
    case "stepRepinned":
      return line({ before: step(change.key, before, true), after: step(change.key, after, true) });
    case "definitionBound":
      return line({ step: step(change.key), abstractStep: abstractStepName(node(after, change.key) ?? node(before, change.key), change.abstractStep), before: <BoundName reference={change.before} />, after: <BoundName reference={change.after} /> });
    case "roleChanged":
      return line({ step: step(change.key) }, { before: t(`workflows.edit.roles.${change.before}`), after: t(`workflows.edit.roles.${change.after}`) });
    case "startChanged":
      return line({ step: step(change.key) }, { before: t(`workflows.edit.starts.${change.before}`), after: t(`workflows.edit.starts.${change.after}`) });
    case "stepMoved":
      return line({ step: step(change.key) });
    case "bindingAdded":
    case "bindingRemoved":
      return line({ target: port(change.key, change.slot, "input", change.kind === "bindingRemoved" ? before : after), source: source(change.from, change.kind === "bindingRemoved" ? before : after) });
    case "bindingChanged":
      return line({ target: port(change.key, change.slot, "input"), before: source(change.before, before), after: source(change.after) });
    case "exportAdded":
    case "exportRemoved":
      return line({ source: source(change.from, change.kind === "exportRemoved" ? before : after) }, { name: change.name });
    case "exportChanged":
      return line({ before: source(change.before, before), after: source(change.after) }, { name: change.name });
    case "textsChanged":
      return <>{t("workflows.changes.textsChanged")}</>;
    case "defaultAdded":
    case "defaultRemoved":
      return line({ target: destination(change.destination, change.kind === "defaultRemoved" ? before : after) }, { value: shownValue(change.value) });
    case "defaultChanged":
      return line({ target: destination(change.destination) }, { before: shownValue(change.before), after: shownValue(change.after) });
  }
}

/** The `workflows.changes` line of each kind of added placement (ADR-0043 D1). */
const ADDED: Readonly<Record<PlacedUse["kind"], string>> = {
  step: "stepAdded",
  workflow: "subWorkflowAdded",
  abstract: "abstractStepAdded",
};

/** A placement's pinned version, or an abstract step's label (ADR-0043 D1). */
export function PlacedUseName({ use, link = true }: { use: PlacedUse; link?: boolean }) {
  return use.kind === "abstract" ? <>{use.label}</> : <ItemReferenceName reference={use.reference} link={link} />;
}

/** An abstract step a use binds, by its label in the nested process schema; its key, kept technical, where that schema isn't read. */
function abstractStepName(use: GraphNode | undefined, abstractStep: string): ReactElement {
  const declared = use?.kind === "sub_workflow" ? use.workflow?.steps.find((step) => step.kind === "abstract" && step.key === abstractStep) : undefined;
  return declared?.kind === "abstract" ? <>{declared.label}</> : <code>{abstractStep}</code>;
}

function BoundName({ reference }: { reference: string | null }) {
  const { t } = useTranslation();
  return reference === null ? <>{t("workflows.changes.unbound")}</> : <ItemReferenceName reference={reference} />;
}
